import { useState, useRef, useEffect, useCallback } from "react";
import { FaSearch, FaChevronDown } from "react-icons/fa";

const DEFAULT_OPTION = { text: "---", value: "default" };
const MOBILE_BREAKPOINT = 768;

let openSelectCount = 0;
let originalBodyOverflow = null;
let originalBodyPaddingRight = null;

const SearchableSelect = ({
  label,
  options,
  value,
  onChange,
  type = "TEXT",
  formatOption,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const isMobile = () => window.innerWidth <= MOBILE_BREAKPOINT;

  const getOptionLabel = (option) => {
    if (!option || option.value === undefined) return "";
    if (option.value === "default") return isMobile() ? "\u00A0" : "---";
    if (formatOption) return formatOption(option);
    if (type === "INT") return String(parseInt(option.text, 10) || option.text);
    if (type === "FLOAT") return String(parseFloat(option.text) || option.text);
    return option.text || String(option.value);
  };

  const allOptions = [DEFAULT_OPTION, ...(options || [])];

  const selectedOption =
    value === "default" || value === null || value === undefined
      ? DEFAULT_OPTION
      : allOptions.find(
          (opt) =>
            opt.value !== "default" &&
            (String(opt.value) === String(value) ||
              (type === "INT" &&
                parseInt(opt.value, 10) === parseInt(value, 10)) ||
              (type === "FLOAT" &&
                parseFloat(opt.value) === parseFloat(value)))
        ) || DEFAULT_OPTION;

  const filtered = allOptions.filter((opt) => {
    if (!search) return true;
    const text = getOptionLabel(opt).toLowerCase();
    return text.includes(search.toLowerCase());
  });

  const open = () => {
    setIsOpen(true);
    setSearch("");
    setHighlightIndex(-1);
    requestAnimationFrame(() => searchInputRef.current?.focus());
  };

  const close = useCallback(() => {
    setIsOpen(false);
    setSearch("");
    setHighlightIndex(-1);
  }, []);

  const select = (option) => {
    try {
      onChange(option.value === "default" ? "default" : option.value);
    } finally {
      close();
    }
  };

  /** Close when controlled value updates (e.g. URL changes after sort navigation). */
  useEffect(() => {
    close();
  }, [value, close]);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDownOutside = (e) => {
      const el = containerRef.current;
      const target = e.target;
      if (!el || !(target instanceof Node)) return;
      if (!el.contains(target)) close();
    };
    /** Capture phase + pointer events so touch and outside clicks close reliably (mousedown alone misses many touch cases). */
    document.addEventListener("pointerdown", handlePointerDownOutside, true);
    return () =>
      document.removeEventListener("pointerdown", handlePointerDownOutside, true);
  }, [isOpen, close]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, close]);

  useEffect(() => {
    if (!isOpen) return;
    const handleResize = () => {
      if (!isMobile() && containerRef.current) {
        containerRef.current
          .querySelector(".ss-dropdown")
          ?.style.removeProperty("transform");
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isOpen]);

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        open();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev < filtered.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev > 0 ? prev - 1 : filtered.length - 1
      );
    } else if (e.key === "Enter" && highlightIndex >= 0) {
      e.preventDefault();
      select(filtered[highlightIndex]);
    }
  };

  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightIndex];
      item?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightIndex]);

  useEffect(() => {
    if (!isOpen) return;
    openSelectCount += 1;
    if (openSelectCount === 1) {
      const body = document.body;
      const docEl = document.documentElement;
      originalBodyOverflow = body.style.overflow;
      originalBodyPaddingRight = body.style.paddingRight;

      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        const currentPadding = parseFloat(
          window.getComputedStyle(body).paddingRight || "0"
        );
        body.style.paddingRight = `${currentPadding + scrollbarWidth}px`;
      }
      body.style.overflow = "hidden";

      if (isMobile()) {
        docEl.classList.add("ss-scroll-lock");
        body.classList.add("ss-scroll-lock");
      }
    }
    return () => {
      openSelectCount = Math.max(0, openSelectCount - 1);
      if (openSelectCount === 0) {
        const body = document.body;
        const docEl = document.documentElement;
        body.style.overflow = originalBodyOverflow ?? "";
        body.style.paddingRight = originalBodyPaddingRight ?? "";
        originalBodyOverflow = null;
        originalBodyPaddingRight = null;
        docEl.classList.remove("ss-scroll-lock");
        body.classList.remove("ss-scroll-lock");
      }
    };
  }, [isOpen]);

  const displayValue =
    selectedOption.value === "default" && label
      ? label
      : getOptionLabel(selectedOption);

  return (
    <div className="ss-container" ref={containerRef}>
      <button
        type="button"
        className={`ss-trigger ${isOpen ? "ss-trigger--open" : ""}`}
        onClick={() => (isOpen ? close() : open())}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span
          className={`ss-trigger-text ${selectedOption.value === "default" ? "ss-trigger-text--placeholder" : ""}`}
        >
          {displayValue}
        </span>
        <FaChevronDown className="ss-trigger-arrow" />
      </button>

      {isOpen && (
        <>
          {isMobile() && (
            <div
              className="ss-overlay"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                close();
              }}
            />
          )}
          <div
            className={`ss-dropdown ${isMobile() ? "ss-dropdown--mobile" : ""}`}
            role="listbox"
          >
            <div className="ss-search-wrap">
              <FaSearch className="ss-search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                className="ss-search-input"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setHighlightIndex(-1);
                }}
                onKeyDown={handleKeyDown}
                placeholder={label || "---"}
                autoComplete="off"
              />
            </div>
            <ul className="ss-options" ref={listRef}>
              {filtered.length === 0 ? (
                <li className="ss-option ss-option--empty">&nbsp;</li>
              ) : (
                filtered.map((opt, i) => {
                  const isSelected =
                    String(opt.value) === String(selectedOption.value);
                  const isHighlighted = i === highlightIndex;
                  const isDefault = opt.value === "default";
                  return (
                    <li
                      key={`${opt.value}-${i}`}
                      role="option"
                      aria-selected={isSelected}
                      className={[
                        "ss-option",
                        isDefault && "ss-option--default",
                        isSelected && "ss-option--selected",
                        isHighlighted && "ss-option--highlighted",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        select(opt);
                      }}
                      onMouseEnter={() => setHighlightIndex(i)}
                    >
                      {getOptionLabel(opt)}
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default SearchableSelect;
